import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, BarChart2, Shield, Award, Users } from "lucide-react";

const LOGO = "/sneha-logo.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const InvestmentInsights = () => {
  const articles = [
    {
      category: "Market Report",
      title: "Navi Mumbai Real Estate: 2025 Investment Outlook",
      excerpt: "With NMIA airport nearing completion and Atal Setu operational, the Navi Mumbai market is poised for its strongest appreciation cycle yet. Here's what investors need to know.",
      readTime: "8 min read",
      date: "Feb 2025",
      icon: TrendingUp,
    },
    {
      category: "Infrastructure Analysis",
      title: "Airport Influence Zone: Where to Buy Now",
      excerpt: "The 25km airport influence zone isn't uniform. We map out the micro-markets with the highest appreciation potential based on current infrastructure progress.",
      readTime: "6 min read",
      date: "Jan 2025",
      icon: BarChart2,
    },
    {
      category: "Investment Guide",
      title: "Pre-Launch vs Ready-to-Move: Which is Right for You?",
      excerpt: "Pre-launch properties offer 20-40% lower entry pricing but carry construction risk. Ready properties offer immediate rental income. A framework to choose wisely.",
      readTime: "5 min read",
      date: "Jan 2025",
      icon: Shield,
    },
    {
      category: "Developer Report",
      title: "Godrej vs Hiranandani vs Adani: Performance Comparison",
      excerpt: "How do Navi Mumbai's top developers compare on delivery timelines, construction quality, and post-possession appreciation? An unbiased analysis.",
      readTime: "10 min read",
      date: "Dec 2024",
      icon: Award,
    },
    {
      category: "NRI Guide",
      title: "NRI Investment Guide: Navi Mumbai 2025",
      excerpt: "From FEMA regulations to repatriation of funds, a comprehensive guide for NRI investors looking to capitalize on Navi Mumbai's growth story.",
      readTime: "12 min read",
      date: "Dec 2024",
      icon: Users,
    },
    {
      category: "ROI Analysis",
      title: "Rental Yield Map: Best Zones in Navi Mumbai",
      excerpt: "Seawoods leads with 5.5% yields. Panvel averages 4.2%. Ulwe offers future upside. A detailed rental yield analysis to help you decide.",
      readTime: "7 min read",
      date: "Nov 2024",
      icon: TrendingUp,
    },
  ];

  const reports = [
    { title: "Navi Mumbai Growth Report 2025", pages: "28 pages", format: "PDF" },
    { title: "Airport Impact Analysis Report", pages: "16 pages", format: "PDF" },
    { title: "10-Year Investment Outlook", pages: "34 pages", format: "PDF" },
    { title: "NAINA Smart City Blueprint", pages: "22 pages", format: "PDF" },
  ];

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Header */}
      <div className="py-16 border-b border-white/5"
        style={{ background: "linear-gradient(180deg, #0a0900 0%, #080808 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label">
            Market Intelligence
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
            Investment Insights<br />
            <span className="text-gold-gradient italic">by Sneha Reality</span>
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {/* Articles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {articles.map((a, i) => (
            <motion.div key={a.title}
              {...fadeUp} transition={{ delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="gold-card glass-gold p-6 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 border border-[#D4AF37]/20 flex items-center justify-center">
                  <a.icon size={13} className="text-[#D4AF37]" />
                </div>
                <span className="text-[9px] tracking-[0.2em] text-[#D4AF37] uppercase">{a.category}</span>
              </div>
              <h3 className="font-heading text-[#FAF8F3] font-light text-base mb-3 flex-1 leading-snug">{a.title}</h3>
              <p className="text-[#666] text-xs leading-relaxed mb-4">{a.excerpt}</p>
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <span className="text-[10px] text-[#444]">{a.date} · {a.readTime}</span>
                <Link to="/consultation"
                  className="text-[10px] text-[#D4AF37] hover:text-[#FAF8F3] transition-colors tracking-wider uppercase">
                  Read More →
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Wealth Reports */}
        <motion.div {...fadeUp} className="glass-gold p-8 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div>
              <p className="section-label mb-2">Wealth Reports</p>
              <h2 className="font-display text-3xl text-[#FAF8F3] font-light">
                Download Our<br />
                <span className="text-gold-gradient italic">Research Reports</span>
              </h2>
            </div>
            <p className="text-[#666] text-sm max-w-xs">
              Data-driven investment research compiled by our market intelligence team. Free for serious investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map((r, i) => (
              <motion.div key={r.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="flex items-center justify-between border border-white/5 p-5 hover:border-[#D4AF37]/20 transition-all duration-300 group"
              >
                <div>
                  <p className="text-sm text-[#FAF8F3] font-light group-hover:text-[#D4AF37] transition-colors">{r.title}</p>
                  <p className="text-xs text-[#444] mt-1">{r.pages} · {r.format}</p>
                </div>
                <Link to="/contact"
                  className="text-[10px] border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-2 hover:bg-[#D4AF37]/10 transition-all tracking-wider uppercase shrink-0 ml-4">
                  Download
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div {...fadeUp} className="mt-16 text-center">
          <img src={LOGO} alt="Sneha Reality" className="h-14 w-auto object-contain mx-auto mb-6" />
          <h3 className="font-display text-2xl text-[#FAF8F3] mb-3">Get Personalized Investment Research</h3>
          <p className="text-[#666] text-sm mb-6 max-w-md mx-auto">
            Our advisors prepare custom research reports tailored to your budget, goals, and risk profile.
          </p>
          <Link to="/consultation" className="btn-gold-solid btn-gold text-xs py-3 px-8">
            Request Custom Report <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default InvestmentInsights;
